import { InjectionToken } from '@angular/core';
import { ConfigurationType } from '@icc/shared/types';
import { Observable, Subject } from 'rxjs';

/**
 * Unique identifier for a configurator instance.
 * Used to scope services and store slices.
 */
export interface ConfiguratorContext {
    /**
     * Unique instance ID (UUID or sequential)
     * Generated when configurator is opened
     */
    readonly instanceId: string;

    /**
     * The configuration being edited (position ID, or null for new)
     */
    readonly configurationId: string | null;

    /**
     * Configurator type: 'external_venetian', 'roller_shutter', etc.
     */
    readonly type: ConfigurationType;

    /**
     * Sequence number - increments each time a new configuration is loaded
     * Used to detect configuration changes even when product IDs are the same
     */
    readonly sequence: number;

    /**
     * Observable that emits when context is about to be destroyed
     * Services can subscribe to clean up resources
     */
    readonly destroy$: Observable<void>;
}

/**
 * Mutable version of ConfiguratorContext for internal use
 */
export interface MutableConfiguratorContext extends ConfiguratorContext {
    incrementSequence(): number;
    destroy(): void;
}

/**
 * Injection token for configurator context
 */
export const CONFIGURATOR_CONTEXT = new InjectionToken<ConfiguratorContext>(
    'CONFIGURATOR_CONTEXT'
);

/**
 * Factory to create a new configurator context
 */
export function createConfiguratorContext(
    type: ConfigurationType,
    configurationId: string | null = null
): MutableConfiguratorContext {
    const destroySubject = new Subject<void>();
    let destroyed = false;
    let sequence = 0;

    return {
        instanceId: generateInstanceId(),
        configurationId,
        type,
        get sequence() {
            return sequence;
        },
        destroy$: destroySubject.asObservable(),
        incrementSequence(): number {
            return ++sequence;
        },
        destroy(): void {
            if (!destroyed) {
                destroyed = true;
                destroySubject.next();
                destroySubject.complete();
            }
        }
    };
}

/**
 * Generates a unique instance ID
 */
function generateInstanceId(): string {
    return `cfg_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Default context for backward compatibility
 * Used when no context is provided (legacy code paths)
 */
export const DEFAULT_CONFIGURATOR_CONTEXT: ConfiguratorContext = {
    instanceId: 'default',
    configurationId: null,
    type: null as any,
    sequence: 0,
    destroy$: new Subject<void>().asObservable(),
};
